import { useId, useCallback, useEffect, useState, type FC } from "react";
import { useDebounce } from "use-debounce";
import { isInvalidArgumentError, isNotFoundError, isUnauthorizedError } from "@/queries/errors";
import InputWithSuggestions from "@/components/podkit/forms/InputWithSuggestions";
import { Label } from "@/components/podkit/forms/Label";
import { type PropsWithClassName, cn } from "@/components/podkit/lib/cn";
import { useListContextUrls } from "@/hooks/use-context-url";
import { TrackLocations } from "@/hooks/use-segment";
import { useParseContextURL } from "@/queries/runner-queries";
import { type GitConfig } from "@/utils/git-config";

export type ParsedGitInfo = {
    cloneUrl: string;
    branch?: string;
    commit?: string;
    repo?: string;
    upstreamRemoteUrl?: string;
};

export type RepositoryInputMode = "create" | "settings";

/**
 * Result object returned by RepositoryInput when using result-based onChange
 */
export type RepositoryInputResult = {
    /** Whether the URL is valid */
    isValid: boolean;
    /** The original URL entered by the user */
    originalUrl: string;

    /** Parsed clone URL (e.g., https://github.com/user/repo.git) */
    cloneUrl?: string;
    /** Resolved branch name from PR/MR or direct branch reference */
    branch?: string;
    /** Specific commit SHA if applicable */
    commit?: string;
    /** Full parsed repository information */
    parsedInfo?: ParsedGitInfo;
    /** Whether there was a parsing error (e.g., PR not found, unauthorized) */
    hasParsingError?: boolean;
};

type Props = {
    /** Component mode: 'create' for simple validation, 'settings' for enhanced parsing */
    mode?: RepositoryInputMode;
    /** Input label text */
    label?: string;
    /** Current input value */
    value?: string;
    /** HTML input name attribute */
    name?: string;
    /** Whether the input is disabled */
    disabled?: boolean;
    /** External error message to display */
    errorMessage?: string;
    /** Whether to auto-focus the input */
    autoFocus?: boolean;
    /** Runner ID for backend parsing (enables advanced URL parsing) */
    runnerId?: string;
    /** Whether to reserve space for error message when no error is present */
    reserveErrorSpace?: boolean;

    /**
     * Change handler - supports two modes:
     * - Result-based: (result: RepositoryInputResult) => void
     * - String-based: (value: string) => void (when using specialized callbacks)
     */
    onChange?: ((result: RepositoryInputResult) => void) | ((value: string) => void);

    // Specialized callback props for specific use cases
    /** Called when git configuration is parsed/updated */
    onGitConfigChange?: (gitConfig: GitConfig | null) => void;
    /** Called when URL validation state changes */
    onValidationChange?: (isValid: boolean) => void;
    /** Called when parsed repository information changes */
    onParsedInfoChange?: (parsedInfo: ParsedGitInfo | null) => void;
    /** Called when parsing state (loading, errors, etc.) changes */
    onParsingStateChange?: (state: {
        isLoading: boolean;
        isSuccess: boolean;
        error: Error | null;
        isNotFound: boolean;
        isInvalidArgument: boolean;
        parseContextFailed: boolean;
        isTypingUrl: boolean;
    }) => void;
};

/**
 * RepositoryInput - A smart input component for repository URLs
 *
 * Features:
 * - URL format validation
 * - Advanced parsing of PR/MR URLs to clone URLs (when runnerId provided)
 * - Two modes: 'create' (simple) and 'settings' (enhanced)
 * - Two API styles: result-based onChange or specialized callbacks
 *
 * Usage:
 * ```tsx
 * // Result-based (new style)
 * <RepositoryInput
 *   mode="settings"
 *   onChange={(result) => console.log(result.cloneUrl)}
 * />
 *
 * // Callback-based (existing style)
 * <RepositoryInput
 *   onValidationChange={setIsValid}
 *   onGitConfigChange={setGitConfig}
 * />
 * ```
 */
export const RepositoryInput: FC<Props & PropsWithClassName> = ({
    mode = "create", // Default to create mode for backward compatibility
    label = "Repository URL",
    value = "",
    onChange,
    onGitConfigChange,
    onValidationChange,
    onParsedInfoChange,
    onParsingStateChange,
    name,
    disabled,
    className,
    errorMessage: externalErrorMessage,
    autoFocus,
    runnerId,
    reserveErrorSpace = true, // Default to true for backward compatibility
}) => {
    const { contextUrls } = useListContextUrls();
    const elId = useId();

    const [inputValue, setInputValue] = useState(value);

    // Initialize isValidURL based on the initial value instead of defaulting to false
    const [isValidURL, setIsValidURL] = useState(() => {
        if (!value) return false;
        try {
            return value.trim() !== "" && new URL(value.toLowerCase()).pathname.length > 3;
        } catch {
            return false;
        }
    });

    // Update internal state when external value changes
    useEffect(() => {
        setInputValue(value);
    }, [value]);

    // Validate URL format
    useEffect(() => {
        try {
            const valid = inputValue.trim() !== "" && new URL(inputValue.toLowerCase()).pathname.length > 3;
            setIsValidURL(valid);
            onValidationChange?.(valid);
        } catch {
            setIsValidURL(false);
            onValidationChange?.(false);
        }
    }, [inputValue, onValidationChange]);

    // Debounce the input to prevent too many API calls while typing
    const [debouncedInput] = useDebounce(inputValue, 500);

    // Parse when runnerId is provided and URL is valid (both create and settings modes)
    // Don't parse invalid URLs as they will cause the query to hang
    const shouldParse = !!runnerId && !!debouncedInput && isValidURL;

    // Parse the context URL using the runner bridge when runnerId is available
    const {
        data: parsedContext,
        isLoading: isLoadingParseContext,
        error: parseError,
    } = useParseContextURL(runnerId, debouncedInput, {
        enabled: shouldParse,
    });

    // Handle input changes
    const handleChange = useCallback((newValue: string) => {
        const trimmedValue = newValue.trim();
        setInputValue(trimmedValue);
    }, []);

    // Detect if onChange expects a string (callback-based) or RepositoryInputResult (result-based)
    // Callback-based mode is when any of the specialized callbacks are provided
    const isCallbackBasedMode = !!(
        onGitConfigChange ||
        onValidationChange ||
        onParsedInfoChange ||
        onParsingStateChange
    );

    // Handle input changes and call appropriate onChange
    useEffect(() => {
        if (!onChange) return;

        if (isCallbackBasedMode) {
            // Callback-based mode: just pass the string value
            (onChange as (value: string) => void)(inputValue);
        } else {
            // Result-based mode: pass RepositoryInputResult with parsed data
            const result: RepositoryInputResult = {
                isValid: isValidURL,
                originalUrl: inputValue,
                hasParsingError: !!parseError,
            };

            // Add parsed data if available (regardless of mode)
            if (parsedContext?.git) {
                result.cloneUrl = parsedContext.git.cloneUrl;
                result.branch = parsedContext.git.branch;
                result.commit = parsedContext.git.commit;
                result.parsedInfo = {
                    cloneUrl: parsedContext.git.cloneUrl,
                    branch: parsedContext.git.branch,
                    commit: parsedContext.git.commit,
                    repo: parsedContext.git.repo,
                    upstreamRemoteUrl: parsedContext.git.upstreamRemoteUrl,
                };
            } else if (isValidURL && !isLoadingParseContext && !parseError) {
                // Fallback: use original URL as clone URL (only if no parsing error)
                result.cloneUrl = inputValue;
                result.branch = undefined; // No branch info available
            }

            (onChange as (result: RepositoryInputResult) => void)(result);
        }
    }, [mode, inputValue, isValidURL, parsedContext, isLoadingParseContext, parseError, onChange, isCallbackBasedMode]);

    // Legacy: Update git config and parsed info when parsing results change
    useEffect(() => {
        if (parsedContext?.git) {
            const parsedInfo: ParsedGitInfo = {
                cloneUrl: parsedContext.git.cloneUrl,
                branch: parsedContext.git.branch,
                commit: parsedContext.git.commit,
                repo: parsedContext.git.repo,
                upstreamRemoteUrl: parsedContext.git.upstreamRemoteUrl,
            };
            onParsedInfoChange?.(parsedInfo);

            const gitConfig: GitConfig = {
                remoteUri: parsedContext.git.cloneUrl,
                cloneTarget: parsedContext.git.branch || parsedContext.git.commit || "main",
                upstreamRemoteUri: parsedContext.git.upstreamRemoteUrl || undefined,
                checkoutLocation: parsedContext.git.repo || undefined,
            };
            onGitConfigChange?.(gitConfig);
        } else if (isValidURL && !isLoadingParseContext && !parseError) {
            // Fallback: treat as direct Git URL
            onParsedInfoChange?.(null);
            const gitConfig: GitConfig = {
                remoteUri: inputValue,
                cloneTarget: "main", // Default branch
            };
            onGitConfigChange?.(gitConfig);
        } else {
            onParsedInfoChange?.(null);
            onGitConfigChange?.(null);
        }
    }, [
        parsedContext,
        inputValue,
        isValidURL,
        isLoadingParseContext,
        parseError,
        onGitConfigChange,
        onParsedInfoChange,
    ]);

    // Track if user is typing
    const isTypingUrl = debouncedInput !== inputValue;

    // Update parsing state
    useEffect(() => {
        if (onParsingStateChange) {
            const isNotFound = isNotFoundError(parseError);
            const isInvalidArgument = isInvalidArgumentError(parseError);
            const parseContextFailed = !isLoadingParseContext && isUnauthorizedError(parseError);
            const isSuccess = !parseError && !isLoadingParseContext && !!parsedContext;

            onParsingStateChange({
                isLoading: isLoadingParseContext,
                isSuccess,
                error: parseError || null,
                isNotFound,
                isInvalidArgument,
                parseContextFailed,
                isTypingUrl,
            });
        }
    }, [
        isLoadingParseContext,
        parseError,
        parsedContext,
        inputValue,
        debouncedInput,
        onParsingStateChange,
        isTypingUrl,
    ]);

    // Determine error message
    const errorMessage =
        externalErrorMessage ||
        (inputValue && !isValidURL ? "The URL must point to a Git repository." : "") ||
        (parseError ? "Could not parse repository URL" : "");

    return (
        <div className={cn("flex items-center space-x-2", className)}>
            <div className="flex w-full flex-col gap-1">
                <Label htmlFor={elId} className="font-medium">
                    {label}
                </Label>
                <InputWithSuggestions
                    id={elId}
                    data-testid="context-url-input"
                    name={name}
                    value={inputValue}
                    disabled={disabled}
                    suggestions={contextUrls || []}
                    placeholder="https://github.com/microsoft/vscode-remote-try-go"
                    onChange={handleChange}
                    error={errorMessage}
                    reserveErrorSpace={reserveErrorSpace}
                    autoFocus={autoFocus}
                    data-track-location={TrackLocations.ContextUrlInput}
                />
                {mode === "settings" && isLoadingParseContext && (
                    <div className="text-muted-foreground min-h-4 text-sm">Validating repository...</div>
                )}
            </div>
        </div>
    );
};
